import { useState } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';

import { Theme, SxProps, Box, useMediaQuery } from '@mui/material';

import { ActionTabs, ActionTabItem } from '../..';

const HOVER_BUTTON_STRINGS = appStrings.modules.nugget;

type HoverButtonProps = Readonly<{
  isVisible: boolean;
  isAbsolutelyPositioned?: boolean;
  position?: 'flush' | 'default';
  idPrefix?: string;
  hasBookmarked?: boolean;
  handleActionBookmark?: () => void;
  handleActionAddTask?: () => void;
  handleActionShare?: () => void;
  isInMyColleges?: boolean;
  handleActionMyColleges?: () => void;
}>;

export const HoverButton = ({
  isVisible,
  isAbsolutelyPositioned = true,
  position = 'default',
  idPrefix,
  hasBookmarked,
  handleActionBookmark,
  handleActionAddTask,
  handleActionShare,
  isInMyColleges,
  handleActionMyColleges,
}: HoverButtonProps) => {
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const isMobile = useMediaQuery('(hover: none), (max-width: 900px)');

  // on mobile/touch, we want to show the button always since users can't hover
  const visible = isMobile ? true : isVisible;

  const possibleActions: ActionTabItem[] = [];

  if (handleActionBookmark) {
    possibleActions.push({
      id: idPrefix ? `${idPrefix}-bookmark-btn` : undefined,
      icon: hasBookmarked ? 'BookmarkCheck' : 'Bookmark',
      onClick: handleActionBookmark,
    });
  }

  if (handleActionShare) {
    possibleActions.push({
      id: idPrefix ? `${idPrefix}-share-btn` : undefined,
      icon: 'Share06',
      onClick: handleActionShare,
      tooltipOnClickText: HOVER_BUTTON_STRINGS.dialogNuggetActions.linkCopied,
      tooltipPosition: 'top',
    });
  }

  if (handleActionMyColleges) {
    possibleActions.push({
      id: idPrefix ? `${idPrefix}-my-colleges-btn` : undefined,
      icon: isInMyColleges ? 'CheckCircle' : 'GraduationHat02',
      onClick: handleActionMyColleges,
      tooltipHoverText: isInMyColleges
        ? HOVER_BUTTON_STRINGS.dialogNuggetActions.removeFromMyColleges
        : HOVER_BUTTON_STRINGS.dialogNuggetActions.addToMyColleges,
    });
  }

  if (handleActionAddTask) {
    possibleActions.push({
      id: idPrefix ? `${idPrefix}-add-task-btn` : undefined,
      icon: 'BookmarkAdd',
      onClick: handleActionAddTask,
    });
  }

  return (
    <Box sx={hoverButtonSx(visible, isAbsolutelyPositioned, position)}>
      <ActionTabs
        actions={possibleActions}
        showCopySuccess={showCopySuccess}
        onCopySuccessChange={setShowCopySuccess}
        borderColor="Colors/Border/border-primary"
      />
    </Box>
  );
};

const hoverButtonSx =
  (
    isVisible: boolean,
    isAbsolutelyPositioned: boolean,
    position: 'flush' | 'default',
  ): SxProps<Theme> =>
  ({ backrs }) => ({
    transition: 'opacity 0.3s ease-in-out',
    opacity: isVisible ? 1 : 0,

    ...(isAbsolutelyPositioned && {
      position: 'absolute',
      top: position === 'flush' ? backrs.spacing['spacing-none'] : backrs.spacing['spacing-xl'],
      right: position === 'flush' ? backrs.spacing['spacing-none'] : backrs.spacing['spacing-2xl'],
      zIndex: 1,
    }),
  });
